import React from "react";

import {Button} from "@mui/material";
import type {ButtonProps} from "@mui/material";
import {Handshake, Send} from "@vedmarket/ui-kit";
import cx from "classnames";
import styles from "./Buttons.module.scss";
import {COLORS} from "../../../../../config/constants";
import ButtonLabel from "../typography/ButtonLabel";
import {PARTNER_REGISTER_LINK} from "../../constants";
import {MOBILE_BUTTON_PADDING} from './appearance';
import type {ButtonAppearanceProps} from './appearance';

type ContainedButtonProps = ButtonProps & ButtonAppearanceProps & {
    children: React.ReactNode;
    isBig?: boolean;
    icon?: 'handshake' | 'send' | null;
    isWhite?: boolean;
};

function ContainedButton({
    children, icon = 'handshake', isBig, isWhite, className, sx, onClick, type,
    mobileSpacing = 'standard', fixedMobileHeight, comfortableLabel, compactDesktop,
    ...props
}: ContainedButtonProps) {
    const ButtonIcon = icon === 'send' ? Send : Handshake;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);

        if (type !== 'submit' && !event.defaultPrevented) {
            window.open(PARTNER_REGISTER_LINK, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <Button variant='outlined' sx={[
            {
                background: isWhite ? COLORS.WHITE : COLORS.PRIMARY,
                '&.MuiButton-outlined': {
                    color: isWhite ? COLORS.BLACK : COLORS.WHITE,
                    border: `1px solid ${isWhite ? COLORS.WHITE : 'transparent'}`,
                    '& .MuiTouchRipple-root': {
                        color: isWhite ? COLORS.GRAY_DARK : COLORS.PRIMARY_DARK,
                    },
                    '&:hover': {
                        background: isWhite ? COLORS.WHITE : COLORS.PRIMARY_HOVER,
                        border: `1px solid ${isWhite ? COLORS.WHITE : COLORS.PRIMARY_HOVER}`,
                    },
                    '&:active': {
                        background: isWhite ? COLORS.WHITE : COLORS.PRIMARY_ACTIVE,
                        border: `1px solid ${isWhite ? COLORS.WHITE : COLORS.PRIMARY_ACTIVE}`,
                    },
                    '@media (min-width: 1200px)': {
                        padding: '0 24px',
                    },
                },
            },
            {
                '@media screen and (max-width: 575px)': {
                    height: fixedMobileHeight ? '40px' : undefined,
                    minHeight: fixedMobileHeight ? '40px' : 0,
                    padding: MOBILE_BUTTON_PADDING[mobileSpacing],
                    '&.MuiButton-outlined': {
                        borderWidth: 0,
                        '&:hover, &:active': {
                            borderWidth: 0,
                        },
                    },
                },
                '@media (min-width: 1200px)': {
                    height: compactDesktop ? '40px' : undefined,
                    minHeight: compactDesktop ? '40px' : undefined,
                },
            },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]} className={cx(styles.button, className, {
            [styles.buttonBig]: isBig,
        })} onClick={handleClick} type={type} {...props}>
            {icon && (
                <span className={cx(styles.iconMask, styles.foreground)} aria-hidden="true">
                    <ButtonIcon className={styles.icon}/>
                </span>
            )}
            <ButtonLabel className={styles.foreground} comfortable={comfortableLabel}>{children}</ButtonLabel>
        </Button>
    );
}

export default ContainedButton
